import { useEffect, useRef, useState } from "react";

/**
 * CountUp — smoothly animates a number from its previous value to the new value.
 * Uses requestAnimationFrame and an ease-out curve for a premium feel.
 *
 * Props:
 *  - value: number to display
 *  - duration: ms (default 500)
 *  - decimals: how many decimal places to show (default 0)
 *  - separator: thousands separator (default ",")
 *  - locale: passed to toLocaleString (default "en-IN")
 *  - suffix: appended after the number (optional)
 *  - prefix: prepended before the number (optional)
 */
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const CountUp = ({
  value = 0,
  duration = 500,
  decimals = 0,
  locale = "en-IN",
  prefix = "",
  suffix = "",
  className = "",
  testId,
}) => {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const fromRef = useRef(value);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    fromRef.current = display;
    startRef.current = performance.now();

    const target = Number.isFinite(value) ? value : 0;

    const tick = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const next = fromRef.current + (target - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration, decimals]);

  const formatted = display.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span data-testid={testId} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default CountUp;
